import { derefPoint, getFieldBounds } from "common/utils/farm";
import { isWithinBounds } from "portal/utils/geo";
import { jobExplorer, JobSelectionType } from "common/state/jobExplorer";
import { SelectionButton } from "portal/components/map/navigation/SelectionButton";
import { useDispatch, useSelector } from "react-redux";
import { useFarmMapData } from "common/hooks/useFarmMapData";
import { useTranslation } from "react-i18next";
import { Zone } from "protos/farm/farm";
import { ZoneIcon } from "portal/components/ZoneIcon";
import CenterPivotIcon from "common/images/icons/irrigation.svg?react";
import React, { FC, useMemo } from "react";

interface FarmPanelProps {}
export const FarmPanel: FC<FarmPanelProps> = () => {
  const { setSelection, setFocus } = jobExplorer.actions;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const focus = useSelector(jobExplorer.selectors.selectFocus);
  const selection = useSelector(jobExplorer.selectors.selectSelection);
  const farm = useSelector(jobExplorer.selectors.selectFarm);
  const job = useSelector(jobExplorer.selectors.selectJob);

  const farmMapData = useFarmMapData(farm);

  const field = useMemo(() => {
    return farm?.zones.find((z) => z.id?.id === job?.fieldId);
  }, [farm, job]);
  const centerPivot = useMemo(() => {
    // todo: change to it's own zone when the farm definition supports it
    return field?.contents?.field?.centerPivot?.center && farmMapData
      ? {
          ...field.contents.field.centerPivot,
          center: derefPoint(
            farmMapData.pointsById,
            field.contents.field.centerPivot.center
          ),
        }
      : undefined;
  }, [field, farmMapData]);

  const relevantZones = useMemo(() => {
    // co-located zones
    // todo: as of writing, jobs only specify a field. We may consider changing it to specify
    // multiple zones
    const zones: Zone[] = [];
    if (field && farmMapData) {
      const fieldBounds = getFieldBounds(field, farmMapData.pointsById);
      if (farm?.zones && fieldBounds) {
        for (const zone of farm.zones) {
          if (
            zone.id?.id &&
            zone.areas[0]?.polygon?.boundary?.points[0]?.id?.id
          ) {
            const ptId = zone.areas[0].polygon.boundary.points[0].id.id;
            const pt = farmMapData.pointsById[ptId];
            const PADDING_METERS = 100;
            if (pt && isWithinBounds(pt, fieldBounds, PADDING_METERS)) {
              zones.push(zone);
            }
          }
        }
      }
    }
    return zones.length > 0 ? zones : [];
  }, [farm, field, farmMapData]);
  return (
    <div>
      {centerPivot && (
        <SelectionButton
          subSection
          startIcon={<CenterPivotIcon className="h-4 w-4 fill-white" />}
          isFocused={
            focus?.type === JobSelectionType.CENTER_PIVOT &&
            focus.selection.endpointDeviceId === centerPivot.endpointDeviceId
          }
          isSelected={
            selection?.type === JobSelectionType.CENTER_PIVOT &&
            selection.selection.endpointDeviceId ===
              centerPivot.endpointDeviceId
          }
          onMouseEnter={() =>
            dispatch(
              setFocus({
                type: JobSelectionType.CENTER_PIVOT,
                selection: centerPivot,
              })
            )
          }
          onMouseLeave={() => dispatch(setFocus(undefined))}
          onClick={() =>
            dispatch(
              setSelection({
                type: JobSelectionType.CENTER_PIVOT,
                selection: centerPivot,
              })
            )
          }
        >
          {t("views.farms.selectionPanel.centerPivot")}
        </SelectionButton>
      )}
      {relevantZones.map((z) => (
        <SelectionButton
          key={z.id?.id}
          subSection
          startIcon={<ZoneIcon zone={z} />}
          isSelected={
            selection?.type === JobSelectionType.ZONE &&
            selection.selection.id?.id === z.id?.id
          }
          isFocused={
            focus?.type === JobSelectionType.ZONE &&
            focus.selection.id?.id === z.id?.id
          }
          onClick={() =>
            dispatch(
              setSelection({ type: JobSelectionType.ZONE, selection: z })
            )
          }
          onMouseEnter={() =>
            dispatch(setFocus({ type: JobSelectionType.ZONE, selection: z }))
          }
          onMouseLeave={() => dispatch(setFocus(undefined))}
        >
          {z.name}
        </SelectionButton>
      ))}
    </div>
  );
};
